import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface CookieConsentProps {
  onAccept?: (consent: { analytics: boolean; marketing: boolean }) => void;
  onCustomize?: (consent: { analytics: boolean; marketing: boolean }) => void;
}

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (command: string, action: string, parameters: any) => void;
    dataLayer: any[];
  }
}

const CookieConsent = ({ onAccept, onCustomize }: CookieConsentProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: true,
    marketing: true
  });

  // Helper function to update Google Consent Mode
  const updateConsentMode = (consent: { analytics: boolean; marketing: boolean }) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'ad_storage': consent.marketing ? 'granted' : 'denied',
        'ad_user_data': consent.marketing ? 'granted' : 'denied',
        'ad_personalization': consent.marketing ? 'granted' : 'denied',
        'analytics_storage': consent.analytics ? 'granted' : 'denied'
      });
    }
  };

  useEffect(() => {
    // Check if consent is already given and not expired
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    } else {
      try {
        const consentData = JSON.parse(consent);
        const thirteenMonthsAgo = Date.now() - (13 * 30 * 24 * 60 * 60 * 1000); // ~13 months
        
        // Show banner again if consent is older than 13 months
        if (consentData.timestamp < thirteenMonthsAgo) {
          localStorage.removeItem('cookie-consent');
          setIsVisible(true);
        }
      } catch (error) {
        // Invalid consent data, show banner
        localStorage.removeItem('cookie-consent');
        setIsVisible(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = { analytics: true, marketing: true };
    
    // Store consent in localStorage
    localStorage.setItem('cookie-consent', JSON.stringify({
      ...consent,
      timestamp: Date.now()
    }));
    
    // Update Google Consent Mode
    updateConsentMode(consent);
    
    // Call callback if provided
    onAccept?.(consent);
    setIsVisible(false);
  };

  const handleCustomize = () => {
    const consent = { ...preferences };
    
    // Store consent in localStorage
    localStorage.setItem('cookie-consent', JSON.stringify({
      ...consent,
      timestamp: Date.now()
    }));
    
    // Update Google Consent Mode
    updateConsentMode(consent);
    
    // Call callback if provided
    onCustomize?.(consent);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consent = { analytics: false, marketing: false };
    
    // Store consent in localStorage
    localStorage.setItem('cookie-consent', JSON.stringify({
      ...consent,
      timestamp: Date.now()
    }));
    
    // Update Google Consent Mode
    updateConsentMode(consent);
    
    // Call callback if provided
    onCustomize?.(consent);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      exit={{ y: 100 }}
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t shadow-lg"
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          {/* Main Message */}
          <div className="flex-1">
            <p className="text-sm text-gray-600">
              We use cookies to enhance your experience, analyze traffic, and for marketing purposes. 
              By clicking "Accept All", you consent to our use of cookies.
              {!isExpanded && (
                <span className="ml-1">
                  <button 
                    onClick={() => setIsExpanded(true)}
                    className="text-primary hover:text-primary/80 underline"
                  >
                    Learn more about our cookies
                  </button>
                </span>
              )}
              <span className="ml-1">
                Read our{' '}
                <a 
                  href="/privacy-policy" 
                  className="text-primary hover:text-primary/80 underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </span>
            </p>
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              {isExpanded ? 'Hide Settings' : 'Cookie Settings'}
            </button>
            <button
              onClick={handleRejectAll}
              className="bg-gray-200 text-gray-900 px-4 py-2 rounded-md text-sm hover:bg-gray-300 transition-colors"
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptAll}
              className="bg-primary text-white px-4 py-2 rounded-md text-sm hover:bg-primary/90 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>

        {/* Expanded Settings */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="pt-4 border-t mt-4 space-y-4">
                {/* Essential Cookies */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Essential Cookies</h3>
                    <p className="text-xs text-gray-500">Required for the website to function properly</p>
                  </div>
                  <div className="bg-gray-100 px-3 py-1 rounded text-xs">
                    Always Active
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Analytics Cookies</h3>
                    <p className="text-xs text-gray-500">Help us improve our website (Google Analytics, Google Tag Manager)</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Marketing Cookies</h3>
                    <p className="text-xs text-gray-500">Used for targeted advertising (Google Ads, Facebook Pixel)</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    onClick={handleCustomize}
                    className="bg-gray-900 text-white px-4 py-2 rounded-md text-sm hover:bg-gray-800 transition-colors"
                  >
                    Save Preferences
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default CookieConsent; 